import React, { useState, useEffect, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const CreateServiceForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    service_name: "",
    description: "",
    rate: "",
    customer_id: "",
    flat_rate: false,
    service_type: "service", // 'service' or 'mileage'
    mileage_record_not_required: false, // Only applicable when service_type is 'service'
  });

  const [customers, setCustomers] = useState([]);
  const [message, setMessage] = useState("");
  const { tenant_id, user } = useContext(AuthContext);

  useEffect(() => {
    if (!tenant_id) return;

    // Load customers for tenant
    secureAxios
      .get("/api/customers", { params: { tenant_id } })
      .then((res) => setCustomers(res.data))
      .catch((err) => console.error("Error loading customers", err));
  }, [tenant_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("Creating service...");

    try {
      const payload = {
        ...formData,
        tenant_id,
        rate: parseFloat(formData.rate),
        created_by: user?.user_id || "system",
      };

      await secureAxios.post("/api/services", payload);

      setMessage("Service created!");

      // Reset form
      setFormData({
        service_name: "",
        description: "",
        rate: "",
        customer_id: "",
        flat_rate: false,
        service_type: "service",
        mileage_record_not_required: false,
      });

      // Wait briefly to show success message, then go back to list
      setTimeout(() => {
        setMessage("");
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err) {
      console.error("Error creating service", err);
      if (err.response?.status === 400) {
        setMessage(`${err.response.data.error || "Invalid data provided"}`);
      } else if (err.response?.status === 409) {
        setMessage(
          "A service with this name already exists for this customer"
        );
      } else {
        setMessage("Failed to create service.");
      }
    }
  };

  return (
    <div>
      <h2>Create Service</h2>
      {message && <p className="fw-bold">{message}</p>}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Service Name */}
        <div className="col-md-6">
          <label className="form-label">Service Name</label>
          <input
            className="form-control"
            name="service_name"
            value={formData.service_name}
            onChange={handleChange}
            required
            maxLength="100"
          />
        </div>

        {/* Rate */}
        <div className="col-md-6">
          <label className="form-label">
            Rate ($) {formData.flat_rate ? "(Total)" : "(Per Hour)"}
          </label>
          <input
            className="form-control"
            name="rate"
            type="number"
            step="0.001"
            min="0"
            max="9999.999"
            value={formData.rate}
            onChange={handleChange}
            required
          />
        </div>

        {/* Customer Dropdown */}
        <div className="col-12">
          <label className="form-label">Customer</label>
          <select
            className="form-select"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
          >
            <option value="">Select Customer</option>
            {customers.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Service Type Dropdown */}
        <div className="col-md-6">
          <label className="form-label">Type</label>
          <select
            className="form-select"
            name="service_type"
            value={formData.service_type}
            onChange={(e) => {
              const newType = e.target.value;
              setFormData((prev) => ({
                ...prev,
                service_type: newType,
                // Reset mileage_record_not_required if switching to mileage type
                mileage_record_not_required:
                  newType === "mileage" ? false : prev.mileage_record_not_required,
              }));
            }}
            required
          >
            <option value="service">Service</option>
            <option value="mileage">Mileage</option>
          </select>
          <small className="text-muted">
            {formData.service_type === "service"
              ? "Hourly or flat-rate service work"
              : "Travel/mileage reimbursement"}
          </small>
        </div>

        {/* Description */}
        <div className="col-12">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            maxLength="500"
            placeholder="Describe the service..."
          ></textarea>
          <small className="text-muted">
            {formData.description.length}/500 characters
          </small>
        </div>

        {/* Flat Rate Checkbox */}
        <div className="col-12">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              name="flat_rate"
              id="flat_rate"
              checked={formData.flat_rate}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="flat_rate">
              <strong>Flat Rate Service</strong>
              <br />
              <small className="text-muted">
                Check this if the service is billed as a fixed amount regardless
                of hours worked
              </small>
            </label>
          </div>
        </div>

        {/* Mileage Record Not Required Checkbox - Only shown for 'service' type */}
        {formData.service_type === "service" && (
          <div className="col-12">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                name="mileage_record_not_required"
                id="mileage_record_not_required"
                checked={formData.mileage_record_not_required}
                onChange={handleChange}
              />
              <label
                className="form-check-label"
                htmlFor="mileage_record_not_required"
              >
                <strong>Mileage Record Not Required</strong>
                <br />
                <small className="text-muted">
                  Check this if job coaches do not need to log mileage when
                  providing this service (e.g., remote/virtual services)
                </small>
              </label>
            </div>
          </div>
        )}

        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onSuccess && onSuccess()}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-success">
            Save Service
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateServiceForm;